/**
 * @fileoverview Data Repair home with delivery-duplicate and lookup-baseline tabs.
 * @module system-web/pages/DataRepairPage
 *
 * Responsibilities:
 * - Compose the shared Data Repair tab registry.
 * - Keep the selected tab addressable through the URL query string.
 *
 * Dependencies:
 * - react-router-dom for query-state navigation.
 * - ../components/ui/TabPanel for accessible tabs.
 * - Existing delivery page and lookup-baseline tab.
 *
 * Architecture:
 * - Tabs compose local-first feature surfaces without owning their data access.
 *
 * Notes:
 * - Add a future per-department repair by adding one entry to DATA_REPAIR_TABS.
 */
import React, { type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import TabPanel from '../components/ui/TabPanel';
import DeliveryDuplicateRepairPage from './DeliveryDuplicateRepairPage';
import LookupBaselineRepairTab from '../components/dataRepair/LookupBaselineRepairTab';

/**
 * @contract DataRepairTab
 * Purpose: Defines one selectable repair surface in the Data Repair home.
 * Fields: Stable query id, visible label, and composed tab content.
 * Rules: New per-department repair surfaces are registered here once.
 * Sync: URL query `tab` uses the stable id.
 */
export interface DataRepairTab {
  id: string;
  label: string;
  content: ReactNode;
}

/** Shared registry for the supported Data Repair tabs. */
export const DATA_REPAIR_TABS: DataRepairTab[] = [
  { id: 'delivery-duplicates', label: 'Delivery Duplicates', content: <DeliveryDuplicateRepairPage /> },
  { id: 'lookup-baselines', label: 'Lookup Baselines', content: <LookupBaselineRepairTab /> },
];

/**
 * @component DataRepairPage
 * Purpose: Provide one admin home for the available one-time data repair tools.
 * Props: None.
 * Behavior: Uses a controlled TabPanel and persists the active tab in `?tab=`.
 * Data Source: Hybrid composition of the existing local-first repair surfaces.
 * Constraints: Tab selection is limited to the supported query values.
 * Performance: Mounts the active tab content supplied to TabPanel.
 */
export default function DataRepairPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab');
  const activeTab = DATA_REPAIR_TABS.some((tab) => tab.id === requestedTab)
    ? requestedTab as string
    : DATA_REPAIR_TABS[0]?.id ?? '';

  return (
    <div className="flex h-full min-h-0 flex-col">
      <TabPanel
        activeTab={activeTab}
        onTabChange={(tabId) => {
          if (DATA_REPAIR_TABS.some((tab) => tab.id === tabId)) {
            setSearchParams({ tab: tabId });
          }
        }}
        tabs={DATA_REPAIR_TABS}
      />
    </div>
  );
}
